import { useEffect, useState, useMemo, useCallback } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useReduxStore } from "../../hooks/useReduxStore";
import { GetItemsByCategory, GetPlaylistsAction } from "../../store/Actions/action";
import { scrollToTop, sorting } from "../../helper/helper";
import { updateHome } from "../../store/Reducers/homeReducer";
import { updateItem } from "../../store/Reducers/itemReducer";
import {
  ACCESS_TYPES,
  ACCESS_FILTER_OPTIONS,
  getItemAccessType,
} from "../../helper/accessTypeHelper";

const useProductByCategory = () => {
  const { dispatch, getState } = useReduxStore();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    items,
    selectedCategory,
    totalItemsCount,
    currentPage,
    lastPage,
    perPageCount,
    playlists = [],
    playlistsLoading = false,
  } = getState("Home");
  const { loading } = getState("AUTH");
  const { search } = getState("Item");
  const [sortBy, setSortBy] = useState(null);

  // Helper to extract access_type from URL query or navigation location state
  const parseAccessType = useCallback(() => {
    const raw =
      searchParams.get("access_type") ||
      searchParams.get("accessType") ||
      location?.state?.accessType ||
      location?.state?.access_type ||
      location?.state?.selectedAccessType;
    if (!raw) return ACCESS_TYPES.ALL;
    const normalized = String(raw).toLowerCase().trim();
    return Object.values(ACCESS_TYPES).includes(normalized)
      ? normalized
      : ACCESS_TYPES.ALL;
  }, [searchParams, location?.state]);

  // Helper to extract view mode (tracks vs playlists) from URL query or navigation location state
  const parseViewMode = useCallback(() => {
    const raw =
      searchParams.get("view") ||
      searchParams.get("viewMode") ||
      searchParams.get("tab") ||
      location?.state?.view ||
      location?.state?.viewMode;
    if (raw === "playlists") return "playlists";
    return "tracks";
  }, [searchParams, location?.state]);

  const initialAccessType = parseAccessType();
  const initialViewMode = parseViewMode();

  const [selectedAccessType, setSelectedAccessType] = useState(initialAccessType);
  const [viewMode, setViewMode] = useState(initialViewMode); // "tracks" | "playlists"
  const [cardLayout, setCardLayout] = useState("grid"); // "grid" | "list"
  const [categorySearchQuery, setCategorySearchQuery] = useState("");
  const [playlistSearchQuery, setPlaylistSearchQuery] = useState("");
  const [selectedFilter, setSelectedFilter] = useState(() => {
    const initial = {
      category: {},
      releasedDate: {},
      language: {},
    };
    if (initialAccessType !== ACCESS_TYPES.ALL) {
      const match = ACCESS_FILTER_OPTIONS.find((opt) => opt.id === initialAccessType);
      initial.accessType = match
        ? { id: match.id, name: match.name, label: match.label }
        : { id: initialAccessType, name: initialAccessType };
    }
    return initial;
  });

  // Synchronize state if URL query param or navigation location.state changes
  useEffect(() => {
    const currentFromUrlOrState = parseAccessType();
    if (currentFromUrlOrState !== selectedAccessType) {
      setSelectedAccessType(currentFromUrlOrState);
      let obj = { ...selectedFilter };
      if (currentFromUrlOrState === ACCESS_TYPES.ALL) {
        delete obj.accessType;
      } else {
        const match = ACCESS_FILTER_OPTIONS.find(
          (opt) => opt.id === currentFromUrlOrState
        );
        obj.accessType = match
          ? { id: match.id, name: match.name, label: match.label }
          : { id: currentFromUrlOrState, name: currentFromUrlOrState };
      }
      setSelectedFilter(obj);
    }

    const currentViewFromUrl = parseViewMode();
    if (currentViewFromUrl !== viewMode) {
      setViewMode(currentViewFromUrl);
    }
  }, [parseAccessType, parseViewMode, selectedAccessType, selectedFilter, viewMode]);

  const onViewModeChange = (mode) => {
    const targetMode = mode === "playlists" ? "playlists" : "tracks";
    setViewMode(targetMode);
    const newParams = new URLSearchParams(searchParams);
    if (targetMode === "playlists") {
      newParams.set("view", "playlists");
    } else {
      newParams.delete("view");
      newParams.delete("viewMode");
      newParams.delete("tab");
    }
    setSearchParams(newParams, { replace: true });
  };

  const onSelectAccessType = (typeId) => {
    setSelectedAccessType(typeId);
    let obj = { ...selectedFilter };
    const newParams = new URLSearchParams(searchParams);

    if (typeId === ACCESS_TYPES.ALL) {
      delete obj.accessType;
      newParams.delete("access_type");
      newParams.delete("accessType");
    } else {
      const match = ACCESS_FILTER_OPTIONS.find((opt) => opt.id === typeId);
      obj.accessType = match
        ? { id: match.id, name: match.name, label: match.label }
        : { id: typeId, name: typeId };
      newParams.set("access_type", typeId);
    }
    setSelectedFilter(obj);
    setSearchParams(newParams, { replace: true });
  };

  const onSelect = (item, type) => {
    if (type === "accessType") {
      onSelectAccessType(item?.id || ACCESS_TYPES.ALL);
      return;
    }

    let obj = { ...selectedFilter };
    if (!item || !item.id) {
      delete obj[type];
    } else {
      obj[type] = item;
    }
    setSelectedFilter({ ...obj });
  };

  const onResetAccessFilter = () => {
    onSelectAccessType(ACCESS_TYPES.ALL);
  };

  const getItems = (page) => {
    let param = {
      id: selectedCategory?.id || 1,
      page: page || currentPage,
      category: selectedFilter?.category?.id,
      released_date: selectedFilter?.releasedDate?.id,
      language: selectedFilter?.language?.title,
      search: search?.length ? search : null,
      access_type:
        selectedAccessType !== ACCESS_TYPES.ALL ? selectedAccessType : null,
    };

    dispatch(GetItemsByCategory(param));
    scrollToTop();
  };

  useEffect(() => {
    getItems(1);
  }, [selectedCategory, search]);

  const onSorting = (item) => {
    setSortBy(item);
  };

  const onUnMount = () => {
    setSortBy(null);
    setSelectedAccessType(ACCESS_TYPES.ALL);
    setSelectedFilter({
      category: {},
      releasedDate: {},
      language: {},
    });
    dispatch(updateHome({ selectedCategory: null, items: [], currentPage: 1 }));
    dispatch(updateItem({ search: "" }));
  };

  const accessTypeCounts = useMemo(() => {
    const counts = {
      [ACCESS_TYPES.ALL]: items?.length || 0,
      [ACCESS_TYPES.FREE]: 0,
      [ACCESS_TYPES.BUY]: 0,
      [ACCESS_TYPES.PPV]: 0,
      [ACCESS_TYPES.EXCLUSIVE]: 0,
    };
    (items || []).forEach((item) => {
      const access = getItemAccessType(item);
      if (counts[access.id] !== undefined) {
        counts[access.id] += 1;
      }
    });
    return counts;
  }, [items]);

  const listData = useMemo(() => {
    let result = [...(items || [])];

    // 1. Access Type filter
    if (selectedAccessType && selectedAccessType !== ACCESS_TYPES.ALL) {
      result = result.filter((item) => {
        const access = getItemAccessType(item);
        return access.id === selectedAccessType;
      });
    }

    // 2. Subcategory filter
    if (selectedFilter?.category?.id) {
      result = result.filter((item) => {
        const subCat = item?.sub_category;
        if (Array.isArray(subCat)) {
          return subCat.some(
            (sc) =>
              sc?.id === selectedFilter?.category?.id ||
              sc?.name === selectedFilter?.category?.name
          );
        }
        return false;
      });
    }

    // 3. Category Instant Search Query
    if (categorySearchQuery && categorySearchQuery.trim()) {
      const q = categorySearchQuery.toLowerCase().trim();
      result = result.filter((item) => {
        const titleMatch = item?.title?.toLowerCase()?.includes(q);
        const nameMatch = item?.name?.toLowerCase()?.includes(q);
        const authorMatch = item?.author?.toLowerCase()?.includes(q);
        const castMatch = item?.cast?.toLowerCase()?.includes(q);
        let subCatMatch = false;
        if (Array.isArray(item?.sub_category)) {
          subCatMatch = item.sub_category.some((sc) =>
            sc?.name?.toLowerCase()?.includes(q)
          );
        }
        return titleMatch || nameMatch || authorMatch || castMatch || subCatMatch;
      });
    }

    // 4. Sorting
    return sorting(
      result,
      sortBy?.id === 2 ? "desending" : sortBy?.id === 1 ? "assending" : null
    );
  }, [items, selectedAccessType, selectedFilter, sortBy, categorySearchQuery]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (selectedAccessType && selectedAccessType !== ACCESS_TYPES.ALL) count += 1;
    if (selectedFilter?.category?.id) count += 1;
    if (selectedFilter?.releasedDate?.id) count += 1;
    if (selectedFilter?.language?.id) count += 1;
    if (categorySearchQuery && categorySearchQuery.trim()) count += 1;
    return count;
  }, [selectedAccessType, selectedFilter, categorySearchQuery]);

  const onClearAllFilters = () => {
    setSelectedAccessType(ACCESS_TYPES.ALL);
    setSelectedFilter({
      category: {},
      releasedDate: {},
      language: {},
    });
    setCategorySearchQuery("");
    setPlaylistSearchQuery("");
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("access_type");
    newParams.delete("accessType");
    setSearchParams(newParams, { replace: true });
  };

  const fetchPlaylists = () => {
    dispatch(GetPlaylistsAction());
  };

  useEffect(() => {
    fetchPlaylists();
  }, []);

  const filteredPlaylists = useMemo(() => {
    let list = Array.isArray(playlists) ? [...playlists] : [];
    if (playlistSearchQuery && playlistSearchQuery.trim()) {
      const q = playlistSearchQuery.toLowerCase().trim();
      list = list.filter(
        (p) =>
          p?.name?.toLowerCase().includes(q) ||
          p?.description?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [playlists, playlistSearchQuery]);

  useEffect(() => {
    return () => {
      onUnMount();
    };
  }, []);

  return {
    items,
    totalItemsCount,
    currentPage,
    lastPage,
    perPageCount,
    loading,
    selectedCategory,
    sortBy,
    listData,
    selectedFilter,
    selectedAccessType,
    accessTypeCounts,
    accessFilterOptions: ACCESS_FILTER_OPTIONS,
    onSelectAccessType,
    onResetAccessFilter,
    onSelect,
    onSorting,
    getItems,
    // Modern Search & Layout additions
    cardLayout,
    setCardLayout,
    categorySearchQuery,
    setCategorySearchQuery,
    activeFilterCount,
    onClearAllFilters,
    // Playlist additions & Mode Switching
    viewMode,
    setViewMode,
    onViewModeChange,
    tracksCount: totalItemsCount || items?.length || 0,
    playlistsCount: playlists?.length || 0,
    playlists,
    playlistsLoading,
    filteredPlaylists,
    playlistSearchQuery,
    setPlaylistSearchQuery,
    fetchPlaylists,
  };
};

export default useProductByCategory;

